"use client";

import React from "react";
import KinsaButtonFooter from "src/components/KinsaButtonFooter";
import { useSubscriptionCheck } from "src/lib/subscription-gate";
import PaymentProcessingIssueState from "./states/payment-processing-issue";
import { CTA_CLICKED_VALUES } from "@repo/statsig";

interface StartVisitButtonProps {
  className?: string;
  hasVisits?: boolean;
  /**
   * When true, renders just a disabled button without any hooks.
   * Use this on modal pages like /select-patient and /block-visit.
   */
  disabled?: boolean;
  /** Callback to manage payment (used when rendered inside /home) */
  onManagePayment?: () => void;
}

/**
 * Static disabled footer - no hooks, just a disabled button.
 */
const StaticDisabledFooter: React.FC<{ className?: string }> = ({
  className,
}) => (
  <KinsaButtonFooter
    buttonText="START A VISIT"
    eventData={{ action: CTA_CLICKED_VALUES.START_VISIT }}
    className={className}
    disabled
  />
);

/**
 * Client component that handles the start visit button footer.
 * The main "start visit" action is now handled by the inline NEW VISIT
 * button inside the visit list. This component only handles:
 * 1. Disabled state (modal overlays)
 * 2. Subscription issue banners (cancelled / payment_issue)
 */
const StartVisitButtonFooter: React.FC<StartVisitButtonProps> = ({
  className,
  hasVisits,
  disabled = false,
  onManagePayment,
}) => {
  // Early return for disabled state - no hooks needed
  if (disabled) {
    return <StaticDisabledFooter className={className} />;
  }

  return (
    <ActiveStartVisitButtonFooter
      hasVisits={hasVisits}
      onManagePayment={onManagePayment}
    />
  );
};

/**
 * Active footer - only renders subscription issue states when applicable.
 */
const ActiveStartVisitButtonFooter: React.FC<
  Pick<StartVisitButtonProps, "hasVisits" | "onManagePayment">
> = ({ hasVisits, onManagePayment }) => {
  const { subscriptionState } = useSubscriptionCheck();

  // Cancelled state is now handled by inline SubscriptionBanner in visit-list-pure.
  if (subscriptionState === "cancelled") {
    return null;
  }

  if (subscriptionState === "payment_issue") {
    if (!hasVisits) {
      return null;
    }
    return (
      <div className="fixed bottom-0 left-0 right-0 bg-background shadow-2xl">
        <PaymentProcessingIssueState
          className="py-6"
          onManagePayment={onManagePayment}
        />
      </div>
    );
  }

  // The inline NEW VISIT button in the visit list handles the has-visits case.
  // When there are no visits, the button is inline in the empty state.
  return null;
};

export default StartVisitButtonFooter;
